'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useAuth } from '../contexts/AuthContext';
import React, { useState } from 'react';
import { featureFlags } from '@/lib/featureFlags';
import { getFreePreviewCtaHref, landingCopy } from '@/lib/landingPageContent';
import { useWorkspaceNavigation } from '@/contexts/WorkspaceNavigationContext';
import { FaBars } from 'react-icons/fa';

export default function Header() {
  const { isLoggedIn, user, logout } = useAuth();
  const pathname = usePathname();
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const { open: openWorkspaceNavigation } = useWorkspaceNavigation()
  const marketingOnly = featureFlags.marketingOnly
  const isLandingPage = pathname === '/' || pathname.startsWith('/landing/')
  const isGuestPreviewPage = pathname.startsWith('/preview')
  const isWorkspacePage = pathname === '/setup' || pathname === '/catalog' || pathname === '/visibility'
  const logoHref = marketingOnly ? '/' : isLoggedIn ? (user?.setup_completed ? "/catalog" : "/setup") : "/"
  const marketingPrimaryHref = '/#features'
  const waitlistHref = '/join'
  const freePreviewHref = getFreePreviewCtaHref(marketingOnly, featureFlags.guestPreview)

  const handleLogout = async () => {
    setShowProfileDropdown(false);
    await logout();
  };

  return (
    <header
      className={`sticky top-0 z-50 ${isLandingPage ? 'marketing-brand-surface' : ''}`}
      style={{ backgroundColor: '#feeaca' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center ${isLandingPage ? 'h-20' : 'h-16 sm:h-24'}`}>
          <Link href={logoHref} aria-label="Skill Bridger home" className="flex shrink-0 items-center">
            <Image
              src="/brand/skill-bridger-mark.svg"
              alt=""
              width={320}
              height={200}
              className="h-11 w-auto sm:hidden"
            />
            <Image
              src="/brand/skill-bridger-horizontal.svg"
              alt=""
              width={920}
              height={220}
              className={`${isLandingPage ? 'h-11' : 'h-12'} hidden w-auto sm:block`}
            />
          </Link>

          <div className="hidden lg:flex items-center space-x-8">
            <nav className="flex items-center space-x-8">
              {isLoggedIn ? (
                <>
                  {featureFlags.navbarCreate && (
                    <div className="relative">
                      <button
                        onClick={() => setShowCreateDropdown(!showCreateDropdown)}
                        className="text-gray-700 hover:text-primary transition-colors font-medium flex items-center space-x-1"
                      >
                        <span>Create</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showCreateDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-2 z-50">
                          <Link
                            href="/create/linkedin-post"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setShowCreateDropdown(false)}
                          >
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                              <div>
                                <div className="font-medium">LinkedIn Post</div>
                                <div className="text-xs text-gray-500">2 credits</div>
                              </div>
                            </div>
                          </Link>
                          <Link
                            href="/create/newsletter"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setShowCreateDropdown(false)}
                          >
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                              </svg>
                              <div>
                                <div className="font-medium">Newsletter Article</div>
                                <div className="text-xs text-gray-500">5 credits</div>
                              </div>
                            </div>
                          </Link>
                          <Link
                            href="/create/resume"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setShowCreateDropdown(false)}
                          >
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <div>
                                <div className="font-medium">Resume Section</div>
                                <div className="text-xs text-gray-500">2 credits per section</div>
                              </div>
                            </div>
                          </Link>
                          <Link
                            href="/create/portfolio"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setShowCreateDropdown(false)}
                          >
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                              </svg>
                              <div>
                                <div className="font-medium">Portfolio Website</div>
                                <div className="text-xs text-gray-500">3 credits + 2/month</div>
                              </div>
                            </div>
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {featureFlags.navbarBrainstorm && (
                    <Link
                      href="/brainstorm"
                      className="text-gray-700 hover:text-primary transition-colors font-medium"
                    >
                      Brainstorm
                    </Link>
                  )}
                  {featureFlags.navbarPortfolio && (
                    <Link
                      href="/portfolio"
                      className="text-gray-700 hover:text-primary transition-colors font-medium"
                    >
                      My Portfolio
                    </Link>
                  )}
                  {featureFlags.navbarDashboard && (
                    <Link
                      href="/dashboard"
                      className="text-gray-700 hover:text-primary transition-colors font-medium"
                    >
                      Dashboard
                    </Link>
                  )}
                </>
              ) : isLandingPage ? (
                <>
                  <Link
                    href="#how-it-works"
                    className="font-semibold text-gray-700 transition-colors hover:text-primary"
                  >
                    How it works
                  </Link>
                  <Link
                    href="#student-results"
                    className="font-semibold text-gray-700 transition-colors hover:text-primary"
                  >
                    Student results
                  </Link>
                </>
              ) : (
                <>
                  <Link
                      href="/"
                      className="text-gray-700 hover:text-primary transition-colors font-medium"
                    >
                      Home
                  </Link>
                  <Link
                    href="/about"
                    className="text-gray-700 hover:text-primary transition-colors font-medium"
                  >
                    About us
                  </Link>
                  <Link
                    href="/features"
                    className="text-gray-700 hover:text-primary transition-colors font-medium"
                  >
                    Features
                  </Link>
                  <Link
                    href="/pricing"
                    className="text-gray-700 hover:text-primary transition-colors font-medium"
                  >
                    Pricing
                  </Link>
                  <Link
                    href="/contact"
                    className="text-gray-700 hover:text-primary transition-colors font-medium"
                  >
                    Contact Us
                  </Link>
                </>
              )}
            </nav>
            <div className="flex items-center space-x-3">
              {!isLoggedIn && (isLandingPage || isGuestPreviewPage) ? (
                <>
                  <Link
                    href="/auth/login"
                    className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    Sign in
                  </Link>
                  <Link
                    href={freePreviewHref}
                    className="whitespace-nowrap rounded-xl bg-[linear-gradient(135deg,#d53b20_0%,#c9361d_100%)] px-5 py-2.5 text-sm font-bold text-white shadow-[0_10px_20px_rgba(201,54,29,0.2)] transition-transform hover:-translate-y-0.5 active:translate-y-px focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    {landingCopy.primaryCta}
                  </Link>
                </>
              ) : marketingOnly ? (
                <>
                  <Link href={waitlistHref}>
                    <button className="px-6 py-2.5 rounded-lg border border-[#f2593d] font-medium transition-all whitespace-nowrap text-[#f2593d] hover:bg-[#f2593d] hover:text-white" style={{ backgroundColor: 'white' }}>
                      Join Waitlist
                    </button>
                  </Link>
                  <Link href={marketingPrimaryHref}>
                    <button className="px-6 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap shadow-sm text-white" style={{ backgroundColor: '#f2593d' }}>
                      Learn More
                    </button>
                  </Link>
                </>
              ) : isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-primary transition-colors"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-full border border-red-200 bg-[#f2593d] shadow-sm ring-2 ring-red-100">
                      <span className="text-sm font-semibold text-white">
                        {user?.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showProfileDropdown && (
                    <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-2 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      <Link
                        href="/settings"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setShowProfileDropdown(false)}
                      >
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>Settings</span>
                        </div>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span>Log out</span>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link href={waitlistHref}>
                    <button className="px-6 py-2.5 rounded-lg border border-[#f2593d] font-medium transition-all whitespace-nowrap text-[#f2593d] hover:bg-[#f2593d] hover:text-white" style={{ backgroundColor: 'white' }}>
                      Join Waitlist
                    </button>
                  </Link>
                  <Link href={marketingPrimaryHref}>
                    <button className="px-6 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap shadow-sm text-white" style={{ backgroundColor: '#f2593d' }}>
                      Learn More
                    </button>
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="lg:hidden flex items-center space-x-3">
            {!isLoggedIn && (isLandingPage || isGuestPreviewPage) ? (
              <>
                <Link
                  href="/auth/login"
                  className="inline-flex px-2 py-2 text-sm font-semibold text-gray-700 transition-colors hover:text-primary"
                >
                  Sign in
                </Link>
                <Link
                  href={freePreviewHref}
                  className="whitespace-nowrap rounded-xl bg-[linear-gradient(135deg,#d53b20_0%,#c9361d_100%)] px-3.5 py-2.5 text-xs font-bold text-white shadow-sm active:translate-y-px sm:px-4 sm:text-sm"
                >
                  {landingCopy.primaryCta}
                </Link>
              </>
            ) : marketingOnly ? (
              <>
                <Link href={waitlistHref}>
                  <button className="px-4 py-2 rounded-lg border border-[#f2593d] font-medium transition-all text-[#f2593d] hover:bg-[#f2593d] hover:text-white" style={{ backgroundColor: 'white' }}>
                    Join
                  </button>
                </Link>
                <Link href={marketingPrimaryHref}>
                  <button className="px-4 py-2 rounded-lg font-medium transition-all shadow-sm text-white" style={{ backgroundColor: '#f2593d' }}>
                    Learn More
                  </button>
                </Link>
              </>
            ) : isLoggedIn ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  aria-label="Open account menu"
                  aria-expanded={showProfileDropdown}
                  aria-haspopup="menu"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-white/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border border-red-200 bg-[#f2593d] shadow-sm ring-2 ring-red-100">
                    <span className="text-xs font-semibold text-white">
                      {user?.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                </button>
                {showProfileDropdown ? (
                  <div
                    role="menu"
                    aria-label="Account menu"
                    className="absolute right-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                  >
                    <div className="border-b border-gray-100 px-4 py-3">
                      <p className="truncate text-sm font-medium text-gray-900">{user?.name}</p>
                      <p className="truncate text-xs text-gray-500">{user?.email}</p>
                    </div>
                    <Link
                      href="/settings"
                      role="menuitem"
                      className="block px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                      onClick={() => setShowProfileDropdown(false)}
                    >
                      Account settings
                    </Link>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleLogout}
                      className="block w-full px-4 py-3 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                    >
                      Log out
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <Link href={waitlistHref}>
                  <button className="px-4 py-2 rounded-lg border border-[#f2593d] font-medium transition-all text-[#f2593d] hover:bg-[#f2593d] hover:text-white" style={{ backgroundColor: 'white' }}>
                    Join
                  </button>
                </Link>
                <Link href={marketingPrimaryHref}>
                  <button className="px-4 py-2 rounded-lg font-medium transition-all shadow-sm text-white" style={{ backgroundColor: '#f2593d' }}>
                    Learn More
                  </button>
                </Link>
              </>
            )}
          </div>

          {isWorkspacePage ? (
            <div className="lg:hidden">
              <button
                type="button"
                onClick={openWorkspaceNavigation}
                aria-label="Open workspace navigation"
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-slate-700 transition-colors hover:bg-white/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <FaBars className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
